import styled, { css } from 'styled-components';

import {
  HOME,
  HOME_STICKY_HEAD,
  COLORS,
  ACQ_MOBILE,
  FIXED_HEADER,
  HEADER_DESKTOP_PX,
  HEADER_MOBILE_PX,
} from '../../../../theme/tokens';
import { Text } from '../../../../components';

/** Zero-height marker that rides in the document flow right at the header's top
 *  edge (it carries the section's top margin, so the sticky Root sits flush to
 *  it). `useStuckOnPin` watches it cross under the layout top bar to know the
 *  header has pinned. Only rendered in sticky mode. */
export const StickySentinel = styled.div`
  height: 0;
  margin-top: 28px;

  ${ACQ_MOBILE} {
    margin-top: 22px;
  }
`;

/** Section header row — title on the left, optional control (the interval
 *  dropdown) on the right. The top/bottom margins are the page's only vertical
 *  rhythm between sections (there is no container gap).
 *
 *  In `$sticky` mode it pins under the layout top bar and, once pinned (`$stuck`),
 *  morphs into a floating pill: an opaque surface (`::after`), rounded corners and
 *  a large soft shadow, with a solid fill (`::before`) bridging the band up to the
 *  top bar so nothing shows through. The pill is drawn on negative-inset pseudos
 *  so the title + picker never shift as it appears. */
export const Root = styled.div<{ $first: boolean; $sticky?: boolean; $stuck?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 28px 0 14px;
  ${(p) =>
    p.$first &&
    css`
      margin-top: 2px;
    `}

  ${(p) =>
    p.$sticky &&
    css`
      position: sticky;
      /* Under the top bar. The desktop offset omits the support-impersonation strip: the
         layout Root's top padding (--ows-imp-offset) already shifts this sticky element's
         scrollport down by the strip height, so re-adding it here would double-count it and
         drag the pinned pill the strip height too low. The mobile override keeps it (different
         regime). See BackHeader for the full rationale. */
      top: calc(${HEADER_DESKTOP_PX}px + var(--ows-imp-safe-top, env(safe-area-inset-top)));
      z-index: 5;
      /* The sentinel carries the top margin so the pin line is exact. */
      margin-top: 0;

      /* Solid fill bridging the band between the top bar and the pinned row, so
         content scrolling up never shows in the gap (or behind the pill's top
         corners). Mirrors PageShell/BackHeader; fades in with the pill. Sits a
         layer BELOW the pill (z-index -2 < the pill's -1) so it never overpaints
         the pill's rounded top — it's only there to mask content, the pill always
         wins where they overlap. Still above the scrolling cards (this row owns a
         z-index 5 stacking context, so even its negative layers sit over them). */
      &::before {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        bottom: calc(100% - 14px);
        height: calc(${HEADER_DESKTOP_PX}px + env(safe-area-inset-top));
        z-index: -2;
        background: ${COLORS.pageBg};
        opacity: 0;
        transition: opacity ${HOME_STICKY_HEAD.morphMs}ms ${HOME.ease};
        pointer-events: none;
      }

      /* The floating pill behind the title + picker. Negative inset extends it past
         the content so the title/picker never move as it morphs in; z-index -1
         keeps it behind them but above the scrolling cards (this row owns a
         z-index 5 stacking context). */
      &::after {
        content: '';
        position: absolute;
        /* Narrower horizontal inset so the frosted pill + its (tight) shadow stay
           well inside the content card and aren't clipped into a hard edge. */
        inset: -10px -8px;
        z-index: -1;
        box-sizing: border-box;
        border-radius: ${HOME_STICKY_HEAD.pillRadius};
        background: ${HOME_STICKY_HEAD.pillBg};
        border: 1px solid ${HOME_STICKY_HEAD.pillBorder};
        box-shadow: ${HOME_STICKY_HEAD.pillShadow};
        opacity: 0;
        transform: translateY(-6px) scale(0.985);
        transform-origin: top center;
        transition: opacity ${HOME_STICKY_HEAD.morphMs}ms ${HOME.ease},
          transform ${HOME_STICKY_HEAD.morphMs}ms ${HOME.ease};
        pointer-events: none;
      }

      ${FIXED_HEADER} {
        /* Mobile keeps --ows-imp-offset: the fixed top bar overlays the viewport and the
           document is the scroller, so the strip height isn't already in the scrollport. */
        top: calc(${HEADER_MOBILE_PX}px + var(--ows-imp-safe-top, env(safe-area-inset-top)) + var(--ows-imp-offset, 0px));
        &::before {
          height: calc(${HEADER_MOBILE_PX}px + env(safe-area-inset-top));
        }
      }
    `}

  ${(p) =>
    p.$sticky &&
    p.$stuck &&
    css`
      &::before {
        opacity: 1;
      }
      &::after {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    `}

  ${ACQ_MOBILE} {
    margin: 22px 0 12px;
    ${(p) =>
      p.$first &&
      css`
        margin-top: 4px;
      `}
    ${(p) =>
      p.$sticky &&
      css`
        margin-top: 0;
      `}
  }
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
`;

export const Spark = styled.img`
  width: 20px;
  height: 20px;
  display: block;
`;

export const TitleText = styled(Text)`
  font-size: 18px;
  font-weight: 700;
  color: ${HOME.t300};
  letter-spacing: -0.01em;
`;
